/**
 * Default board seed — the instant board every fresh visitor gets.
 *
 * Rules (from brief):
 * - Weather + clock + news + markets tiles (sports omitted — needs a team pick).
 * - All params validated through BoardSchema before storage.
 * - Geolocation: if granted, use the real coords; if denied/unavailable, fall
 *   back to San Francisco silently (no nagging — calm brand, PRODUCT.md §core).
 *
 * Returns a Board that passes loadBoard() with ok:true.
 */
import {
  BOARD_SCHEMA_VERSION,
  TILE_SCHEMA_VERSION,
  loadBoard,
  BOARD_STORAGE_KEY,
  type Board,
} from 'briefpane/schema';

/** SF as the graceful fallback when geolocation is unavailable. */
const SF_LAT = 37.77;
const SF_LON = -122.42;

/** Deterministic tile id generation — simple, bounded, id-pattern-safe. */
function tileId(suffix: string): string {
  return `tile_${suffix}`;
}

/** Build the raw board object for a given lat/lon. */
function buildRawBoard(lat: number, lon: number): unknown {
  const now = new Date().toISOString();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    schemaVersion: BOARD_SCHEMA_VERSION,
    id: 'board_default',
    createdAt: now,
    updatedAt: now,
    grid: { cols: 8, rowHeight: 120, gap: 12 },
    layout: [
      // Row 0: weather (2×2), clock (1×1), news (2×1)
      { tileId: tileId('wx0001'), x: 0, y: 0, w: 2, h: 2 },
      { tileId: tileId('clk001'), x: 2, y: 0, w: 1, h: 1 },
      { tileId: tileId('nws001'), x: 3, y: 0, w: 2, h: 1 },
      // Row 1 (clock's second row is markets)
      { tileId: tileId('mkt001'), x: 2, y: 1, w: 2, h: 1 },
    ],
    tiles: [
      {
        v: TILE_SCHEMA_VERSION,
        id: tileId('wx0001'),
        type: 'weather.current',
        source: {
          provider: 'openweathermap',
          params: { lat, lon, units: 'imperial' },
        },
        refresh: { intervalSec: 900 },
        display: { size: '2x2', variant: 'big-number' },
        glance: { primary: 'tempF', delta: 'vsYesterdayF', state: 'condition' },
        label: 'Weather',
      },
      {
        v: TILE_SCHEMA_VERSION,
        id: tileId('clk001'),
        type: 'clock.local',
        source: { provider: 'local', params: { tz, format: '12h' } },
        display: { size: '1x1', variant: 'big-number' },
        glance: { primary: 'time', delta: 'date', state: null },
        label: 'Clock',
      },
      {
        v: TILE_SCHEMA_VERSION,
        id: tileId('nws001'),
        type: 'news.headlines',
        source: { provider: 'gnews', params: { topic: 'world', count: 5 } },
        refresh: { intervalSec: 600 },
        display: { size: '2x1', variant: 'headline' },
        glance: { primary: 'headline', delta: 'source', state: null },
        label: 'Headlines',
      },
      {
        v: TILE_SCHEMA_VERSION,
        id: tileId('mkt001'),
        type: 'markets.quote',
        source: {
          provider: 'finnhub',
          params: { symbols: ['SPY'], display: 'change' },
        },
        refresh: { intervalSec: 60 },
        display: { size: '2x1', variant: 'big-number' },
        glance: { primary: 'price', delta: 'changePct', state: 'direction' },
        label: 'Markets',
      },
    ],
  };
}

/** Build and validate the default board for the given location. */
function makeDefaultBoard(lat: number, lon: number): Board {
  const raw = buildRawBoard(lat, lon);
  const result = loadBoard(raw);
  if (!result.ok) {
    // This should never happen — the raw object matches the schema by construction.
    // If it does (e.g. a schema change), throw so it's caught in dev immediately.
    throw new Error(`Default board failed schema validation: ${result.detail ?? result.reason}`);
  }
  return result.board;
}

/**
 * Request browser geolocation and return the default board seeded with real or
 * fallback coordinates. Never throws; permission denied → SF fallback silently.
 */
export async function buildDefaultBoard(): Promise<Board> {
  if (!navigator.geolocation) {
    return makeDefaultBoard(SF_LAT, SF_LON);
  }
  return new Promise<Board>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve(makeDefaultBoard(pos.coords.latitude, pos.coords.longitude));
      },
      () => {
        // Permission denied or unavailable — silent fallback (calm brand).
        resolve(makeDefaultBoard(SF_LAT, SF_LON));
      },
      { timeout: 3000, maximumAge: 5 * 60 * 1000 },
    );
  });
}

/**
 * The localStorage key for the persisted board.
 *
 * RE-EXPORTED from the schema package (the ONE place key literals live — see
 * src/schema/storage-keys.ts). Kept exported HERE so the existing app import sites
 * (use-board, use-editable-board, OnboardingBar) keep working unchanged; the literal
 * itself now lives in schema alongside SPACES_STORAGE_KEY so the two can never drift.
 */
export { BOARD_STORAGE_KEY };
